import {Link, useNavigate} from 'react-router-dom';
import {BreadcrumbPart} from '../components/page-title';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications';
import {ROUTES} from '../routes';
import {partnerSiteSchema, PartnerSiteSchema} from './schemas';
import {PartnerSitesService} from '../api/generated/PartnerSitesService';
import {SubmitHandler, useFormContext} from 'react-hook-form';
import {useCallback, useMemo} from 'react';
import {BasicPage} from '../components/basic-page';
import {Button, Divider, Group, Stack, Text} from '@mantine/core';
import {UtcOptions} from '../api/generated/enums';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {AsyncStateContainer} from '../components/async-state-container';
import {FilesService} from '../api/generated/FilesService';
import {ImagePreview} from '../components/image-preview';

const ReportFrequencies = {
  Monthly: 'Monthly',
  Weekly: 'Weekly',
  None: 'None',
} as const;

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Partner Sites', href: ROUTES.ADMIN.PARTNER_SITES.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<PartnerSiteSchema>();

const DEFAULT_VALUES = {
  timeDifference: UtcOptions['UTC +0'],
};

export const PartnerSiteAdd = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<PartnerSiteSchema>>(
    async (value) => {
      const response = await PartnerSitesService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Partner Site Created');
      navigate(ROUTES.ADMIN.PARTNER_SITES.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        schema={partnerSiteSchema}
        onSubmit={onSubmit}
        defaultValues={DEFAULT_VALUES}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify='flex=end'>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.PARTNER_SITES.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Partner Site</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const PartnerSiteEdit = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.PARTNER_SITES.DETAIL);

  const fetchPartnerSite = useAsync(async () => {
    const {data} = await PartnerSitesService.getById({id});
    return data;
  }, [id]);

  const partnerSite = fetchPartnerSite.value || undefined;

  const partnerSiteName = partnerSite?.name ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Partner Sites', href: ROUTES.ADMIN.PARTNER_SITES.INDEX.path},
      {title: partnerSiteName},
    ],
    [partnerSiteName],
  );

  const onSubmit = useCallback<SubmitHandler<PartnerSiteSchema>>(
    async (value) => {
      const response = await PartnerSitesService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Partner Site Updated');
      navigate(ROUTES.ADMIN.PARTNER_SITES.INDEX.path);
    },
    [id, navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchPartnerSite}>
        <Form
          schema={partnerSiteSchema}
          onSubmit={onSubmit}
          defaultValues={partnerSite}
        >
          <Form.Container>
            <FormFields />
            <Group mt={24} justify='flex-end'>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.PARTNER_SITES.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update Partner Site</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  const notifications = useNotification();

  const {watch} = useFormContext<PartnerSiteSchema>();

  const logoUrlWatch = watch('logoUrl');
  const signatureUrlWatch = watch('signatureUrl');
  const arfSealUrlWatch = watch('arfSealUrl');

  const onDrop = useCallback(
    async (file: File | null) => {
      if (!file) {
        return;
      }

      const response = await FilesService.uploadPartnerSiteImage({file});

      if (response.hasErrors) {
        notifications.errors(response.errors);
        return;
      }

      return response.data?.uri;
    },
    [notifications],
  );

  return (
    <>
      <Form.Row>
        <Form.TextInput name='name' label='Name' />
        <Form.TextInput name='arfNumber' label='ARF Number' />
        <Form.Select
          name='timeDifference'
          label='UTC Time Difference'
          enumObject={UtcOptions}
        />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextInput name='address' label='Address' />
        <Form.TextInput name='city' label='City' />
        <Form.TextInput name='state' label='State' />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextInput name='zipCode' label='ZIP Code' />
        <Form.TextInput name='country' label='Country' />
        <Form.NumberInput name='sortOrder' label='Sort Order' />
      </Form.Row>
      <Form.Row mt={12}>
        <Stack>
          <ImagePreview uri={logoUrlWatch} />
          <Form.FileButton name='logoUrl' label='Logo' onChange={onDrop} />
        </Stack>
        <Stack>
          <ImagePreview uri={signatureUrlWatch} />
          <Form.FileButton
            name='signatureUrl'
            label='Signature'
            onChange={onDrop}
          />
        </Stack>
        <Stack>
          <ImagePreview uri={arfSealUrlWatch} />
          <Form.FileButton
            name='arfSealUrl'
            label='ARF Seal'
            onChange={onDrop}
          />
        </Stack>
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextInput name='signatoryName' label='Signatory Name' />
        <Form.TextInput name='signatoryTitle' label='Signatory Title' />
      </Form.Row>
      <Form.Row mt={12} w='50%'>
        <Form.Checkbox
          name='sendEvaluationToActiveUser'
          label='Send Evaluation to Active User'
        />
        <Form.Checkbox name='inactive' label='Inactive' />
      </Form.Row>

      <Divider my={24} />
      <Text fw={600} size='lg' mb={12}>
        S/N Tracking Settings
      </Text>
      <Form.Row>
        <Form.Checkbox
          name='serialNumberTrackingEnabled'
          label='Enable Serial Number Tracking'
        />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextArea
          name='soldToValues'
          label='Sold To IDs'
          description='Comma-separated list of Epicor Customer IDs (e.g. S160, M120) to fetch serial numbers for this partner site'
          autosize
          minRows={2}
        />
      </Form.Row>
      <Form.Row mt={12} w='50%'>
        <Form.Checkbox
          name='allowCosDateOverride'
          label='Allow COS Date Override'
        />
        <Form.Checkbox
          name='allowCosSelfSelection'
          label='Allow COS Date Self-Selection'
        />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.NumberInput
          name='cosCutOffDaysOverride'
          label='COS Cut-Off Days Override'
          description='Override the global COS cut-off days for this partner site. Leave blank to use global default (730 days).'
        />
        <Form.NumberInput
          name='uecocExpirationDaysOverride'
          label='UECOC Expiration Days Override'
          description='Override the global UECOC expiration days for this partner site. Leave blank to use global default (1825 days).'
        />
      </Form.Row>
      <Form.Row mt={12}>
        <Form.TextArea
          name='trackingReportEmails'
          label='Tracking Report Emails'
          description='Comma-separated list of email addresses to receive S/N tracking reports'
          autosize
          minRows={2}
        />
        <Form.Select
          name='reportFrequency'
          label='Report Frequency'
          description='How often S/N tracking reports are sent. Leave blank for monthly (default).'
          enumObject={ReportFrequencies}
          clearable
        />
      </Form.Row>
    </>
  );
};
